import * as React from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";

const CommandContext = React.createContext<{
  listId: string;
  query: string;
  setQuery: (query: string) => void;
} | null>(null);

interface CommandProps extends React.ComponentProps<"div"> {
  value?: string;
  onValueChange?: (value: string) => void;
}

function Command({ className, children, value, onValueChange, ...props }: CommandProps) {
  const [uncontrolledQuery, setUncontrolledQuery] = React.useState("");
  const listId = React.useId();
  const query = value ?? uncontrolledQuery;
  const setQuery = (next: string) => {
    if (value === undefined) setUncontrolledQuery(next);
    onValueChange?.(next);
  };
  return (
    <CommandContext.Provider value={{ listId, query, setQuery }}>
      <div
        className={cn(
          "flex max-h-[min(28rem,calc(100svh-4rem))] flex-col overflow-hidden rounded-[var(--radius-dialog)] border bg-popover text-popover-foreground shadow-dialog",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </CommandContext.Provider>
  );
}

interface CommandInputProps extends React.ComponentProps<"input"> {
  onQueryChange?: (query: string) => void;
}

function CommandInput({ className, onQueryChange, onChange, ...props }: CommandInputProps) {
  const context = React.useContext(CommandContext);
  return (
    <label className="flex h-12 shrink-0 items-center gap-2 border-b px-4 text-muted-foreground">
      <Search className="size-4 shrink-0" aria-hidden />
      <input
        role="combobox"
        aria-autocomplete="list"
        aria-expanded="true"
        aria-controls={context?.listId}
        className={cn(
          "h-full min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground",
          className,
        )}
        value={props.value ?? context?.query}
        onChange={(event) => {
          context?.setQuery(event.currentTarget.value);
          onQueryChange?.(event.currentTarget.value);
          onChange?.(event);
        }}
        {...props}
      />
    </label>
  );
}

function CommandList({ className, ...props }: React.ComponentProps<"div">) {
  const context = React.useContext(CommandContext);
  return (
    <div
      id={context?.listId}
      role="listbox"
      className={cn("overflow-y-auto p-1.5", className)}
      {...props}
    />
  );
}

function CommandEmpty({ className, ...props }: React.ComponentProps<"div">) {
  const context = React.useContext(CommandContext);
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("py-6 text-center text-sm text-muted-foreground", className)}
      data-query={context?.query}
      {...props}
    />
  );
}

function CommandGroup({
  heading,
  className,
  ...props
}: React.ComponentProps<"div"> & { heading?: string }) {
  return (
    <div
      role="group"
      aria-label={heading}
      className={cn("overflow-hidden p-1 text-foreground", className)}
      {...props}
    >
      {heading && (
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{heading}</div>
      )}
      {props.children}
    </div>
  );
}

interface CommandItemProps extends React.ComponentProps<"button"> {
  value?: string;
}

function CommandItem({
  className,
  value,
  children,
  onKeyDown,
  onFocus,
  onBlur,
  ...props
}: CommandItemProps) {
  const context = React.useContext(CommandContext);
  const [focused, setFocused] = React.useState(false);
  const childLabel = React.Children.toArray(children)
    .filter((child) => typeof child === "string")
    .join(" ");
  const label = (value ?? childLabel).toLocaleLowerCase();
  if (context?.query && !label.includes(context.query.toLocaleLowerCase())) return null;
  return (
    <button
      type="button"
      role="option"
      aria-selected={focused}
      className={cn(
        "flex min-h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent",
        className,
      )}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented || (event.key !== "ArrowDown" && event.key !== "ArrowUp"))
          return;
        const options = [
          ...(event.currentTarget
            .closest("[role=listbox]")
            ?.querySelectorAll<HTMLElement>("[role=option]") ?? []),
        ];
        const index = options.indexOf(event.currentTarget);
        const nextIndex =
          event.key === "ArrowDown"
            ? Math.min(index + 1, options.length - 1)
            : Math.max(index - 1, 0);
        event.preventDefault();
        options[nextIndex]?.focus();
      }}
      onFocus={(event) => {
        setFocused(true);
        onFocus?.(event);
      }}
      onBlur={(event) => {
        setFocused(false);
        onBlur?.(event);
      }}
      {...props}
    >
      {children}
    </button>
  );
}

function CommandShortcut({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn("ml-auto font-mono text-xs tracking-widest text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
};
export type { CommandInputProps };
